import { faPhone, faPlus } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useGetPhoneNumbers from '../../api/clients/useGetPhoneNumbers';
import useCreatePhoneNumber from '../../api/clients/useCreatePhoneNumber';
import useUpdatePhoneNumber from '../../api/clients/useUpdatePhoneNumber';
import GenericPageContainer from '../../components/agv-generic-page-container';
import GenericTable from '../../components/agv-generic-table';
import GenericTableSkeleton from '../../components/agv-generic-table/genericTableSkeleton';
import HeaderButton from '../../components/agv-header-button';
import { STATUS_ENUM } from '../../components/agv-status/status-enum';
import CreatePhoneNumberModal from './create-phone-number-modal';
import PhoneNumberModal from './phone-number-modal';

export default function PhoneNumbers() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);

  const {
    phoneNumbers,
    isLoading,
    error: errorGetPhoneNumbers,
    refetch,
  } = useGetPhoneNumbers();

  const {
    isSuccess: isSuccessCreate,
    error: errorCreatePhoneNumber,
    isLoading: isLoadingCreate,
    createPhoneNumber,
    resetStates: resetCreateStates,
  } = useCreatePhoneNumber();

  const {
    isSuccess: isSuccessUpdate,
    error: errorUpdatePhoneNumber,
    isLoading: isLoadingUpdate,
    updatePhoneNumber,
    resetStates: resetUpdateStates,
  } = useUpdatePhoneNumber();

  useEffect(() => {
    if (isSuccessCreate) {
      setIsCreateModalOpen(false);
      refetch();
      toast.success('Numéro de téléphone créé avec succès');
      resetCreateStates();
    }
  }, [isSuccessCreate, refetch, resetCreateStates]);

  useEffect(() => {
    if (isSuccessUpdate) {
      setIsEditModalOpen(false);
      setSelectedPhoneNumber(null);
      refetch();
      toast.success('Numéro de téléphone mis à jour avec succès');
      resetUpdateStates();
    }
  }, [isSuccessUpdate, refetch, resetUpdateStates]);

  useEffect(() => {
    if (errorGetPhoneNumbers) {
      toast.error(errorGetPhoneNumbers);
    }
  }, [errorGetPhoneNumbers]);

  useEffect(() => {
    if (errorCreatePhoneNumber) {
      toast.error(errorCreatePhoneNumber);
    }
  }, [errorCreatePhoneNumber]);

  useEffect(() => {
    if (errorUpdatePhoneNumber) {
      toast.error(errorUpdatePhoneNumber);
    }
  }, [errorUpdatePhoneNumber]);

  const handleCreatePhoneNumber = () => {
    setIsCreateModalOpen(true);
  };

  const handleEditPhoneNumber = (phoneNumber) => {
    setSelectedPhoneNumber(phoneNumber);
    setIsEditModalOpen(true);
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'Non renseigné';
    return phoneNumber.replace(/(\d{2})(?=\d)/g, '$1 ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non renseigné';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <GenericPageContainer
        title={'Gestion des numéros de téléphone'}
        subtitle={
          'Gérez les numéros de téléphone de support disponibles pour votre équipe'
        }
        filters={
          <HeaderButton
            onClick={handleCreatePhoneNumber}
            text={'Ajouter un numéro'}
            icon={faPlus}
          />
        }
      >
        {isLoading ? (
          <GenericTableSkeleton hasTitle />
        ) : (
          <GenericTable
            title={`Numéros de téléphone (${phoneNumbers?.length || 0})`}
            isLoading={isLoading}
            rows={
              phoneNumbers?.map((phoneNumber) => ({
                title: phoneNumber.title,
                subtitle: formatPhoneNumber(phoneNumber.phone_number),
                icon: faPhone,
                onClick: () => handleEditPhoneNumber(phoneNumber),
                buttonText: 'Modifier',
                status: phoneNumber.is_archived
                  ? STATUS_ENUM.NEGATIVE
                  : STATUS_ENUM.POSITIVE,
                statusText: phoneNumber.is_archived ? 'Archivé' : 'Actif',
              })) || []
            }
            iconColor={'tw-text-primary'}
          />
        )}
      </GenericPageContainer>

      {isCreateModalOpen && (
        <CreatePhoneNumberModal
          isOpen={isCreateModalOpen}
          setIsOpen={setIsCreateModalOpen}
          createPhoneNumber={createPhoneNumber}
          mainActionIsLoading={isLoadingCreate}
        />
      )}

      {isEditModalOpen && selectedPhoneNumber && (
        <PhoneNumberModal
          isOpen={isEditModalOpen}
          setIsOpen={setIsEditModalOpen}
          phoneNumber={selectedPhoneNumber}
          updatePhoneNumber={updatePhoneNumber}
          mainActionIsLoading={isLoadingUpdate}
        />
      )}
    </>
  );
}
